import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LinkIcon from '@mui/icons-material/Link';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useNavigate } from 'react-router-dom';

import { ClubTable } from '../../components/club/club-table';
import { ClubDrawer } from '../../components/club/club-drawer';
import { ConfirmationDialog } from '../../components/shared/confirmation-dialog';
import { InviteLinkDialog } from '../../components/shared/InviteLinkDialog';
import { QrLabelsDialog } from '../../components/club/qr-labels-dialog';
import { ClubListDialog } from '../../components/club/club-list-dialog';
import { useUpdateClubMutation } from '../../redux';
import { useAuthContext } from '../../context/auth-context';

const PRIMARY = '#0F6E56';

export const ClubPage = () => {
  const navigate = useNavigate();
  const { activeCamp, hasPermission } = useAuthContext();
  // Un Secretario de Zona tiene `clubs.view` pero no `clubs.manage`: ve el listado y
  // nada más. El servidor ya rechaza sus escrituras; esto evita ofrecérselas.
  const puedeGestionar = hasPermission('clubs.manage');
  const [drawerMode, setDrawerMode] = useState(null); // null | 'create' | 'edit'
  const [selectedClub, setSelectedClub] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [listadoOpen, setListadoOpen] = useState(false);

  const [updateClub] = useUpdateClubMutation();

  const inviteUrl = activeCamp
    ? `${window.location.origin}/club-invite/${activeCamp._id}`
    : '';

  const openDrawer = useCallback((mode, club = null) => {
    setSelectedClub(club);
    setDrawerMode(mode);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedClub(null);
  }, []);

  const handleEdit = useCallback(club => openDrawer('edit', club), [openDrawer]);

  const handleDeleteRequest = useCallback(club => {
    setDeleteTarget(club);
  }, []);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const affiliationId =
      typeof deleteTarget.affiliatedTo === 'object' && deleteTarget.affiliatedTo !== null
        ? deleteTarget.affiliatedTo._id
        : deleteTarget.affiliatedTo;

    await updateClub({
      _id: deleteTarget._id,
      name: deleteTarget.name,
      director: deleteTarget.director,
      subdirector: deleteTarget.subdirector,
      totalAttendance: deleteTarget.totalAttendance,
      affiliatedTo: affiliationId,
      status: false,
    });
    setDeleteTarget(null);
    if (selectedClub?._id === deleteTarget._id) closeDrawer();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100%', gap: 0 }}>
      {/* Main area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Topbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            Clubes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* El enlace de invitación abre un alta pública: es gestión, no lectura. */}
            {puedeGestionar && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => setInviteOpen(true)}
                disabled={!activeCamp}
                sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
              >
                Invitar club
              </Button>
            )}
            {/* Imprimir etiquetas es lectura: le sirve igual a un Secretario de Zona. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<QrCode2Icon />}
              onClick={() => setQrOpen(true)}
              disabled={!activeCamp}
              sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
            >
              Etiquetas QR
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={() => setListadoOpen(true)}
              disabled={!activeCamp}
              sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
            >
              Listado
            </Button>
            {puedeGestionar && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileUploadIcon />}
                onClick={() => navigate('/clubes/import')}
                sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
              >
                Importar
              </Button>
            )}
            {puedeGestionar && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openDrawer('create')}
                sx={{
                  bgcolor: PRIMARY,
                  '&:hover': { bgcolor: '#0a5a45' },
                  textTransform: 'none',
                }}
              >
                Nuevo club
              </Button>
            )}
          </Box>
        </Box>

        <ClubTable
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          selectedId={selectedClub?._id}
        />
      </Box>

      {/* Drawer */}
      <ClubDrawer
        open={drawerMode !== null}
        mode={drawerMode}
        club={selectedClub}
        onClose={closeDrawer}
        onDeleteRequest={handleDeleteRequest}
      />

      {/* Delete confirmation */}
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        title="Deshabilitar club"
        bodyText={`¿Deshabilitar "${deleteTarget?.name}"? El club quedará inactivo pero no se eliminará.`}
        handleSubmit={handleConfirmDelete}
        handleClose={() => setDeleteTarget(null)}
      />

      {/* Listado de inscritos */}
      <ClubListDialog open={listadoOpen} onClose={() => setListadoOpen(false)} />

      {/* Etiquetas QR */}
      <QrLabelsDialog open={qrOpen} onClose={() => setQrOpen(false)} />

      {/* Invite link */}
      <InviteLinkDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        url={inviteUrl}
        title="Invitar club"
        description="Comparte este link con el director del club para que se registre o inicie sesión y se una al campamento."
      />
    </Box>
  );
};
